// /frontend/dashboard/src/router/modules/adminRoutes.js
// Admin route module for the dashboard application.
// English explanations are followed by Thai explanations for route-design validation and debugging.

import { debugLog } from '../../utils/debugGate.js';

const ROUTE_DEBUG_NAMESPACE = '[router/adminRoutes]';

function emitAdminRouteDebug(label, payload) {
  debugLog(label, payload);
}

// Vite-safe page module registry.
// English: import.meta.glob() lets Vite discover candidate page files at build time
//          without requiring every route module to hard-code a single exact path.
//          This is useful while admin pages may live either in src/pages/ or src/pages/admin/.
// ไทย: import.meta.glob() ทำให้ Vite มองเห็นไฟล์ candidate ตอน build ได้
//      โดยไม่ต้อง hard-code path เดียวตายตัวใน route module
//      เหมาะกับช่วงที่ไฟล์ admin pages อาจอยู่ทั้งใน src/pages/ หรือ src/pages/admin/
const PAGE_MODULES = import.meta.glob('../../pages/**/*.vue');

const PAGE_REGISTRY = {
  AdminDashboardPage: [
    '../../pages/AdminDashboardPage.vue',
    '../../pages/admin/AdminDashboardPage.vue',
  ],
  AdminOverviewPage: [
    '../../pages/AdminOverviewPage.vue',
    '../../pages/admin/AdminOverviewPage.vue',
  ],
  AdminUsersPage: [
    '../../pages/AdminUsersPage.vue',
    '../../pages/admin/AdminUsersPage.vue',
  ],
  AdminSettingsPage: [
    '../../pages/AdminSettingsPage.vue',
    '../../pages/admin/AdminSettingsPage.vue',
  ],
  AdminAuditPage: [
    '../../pages/AdminAuditPage.vue',
    '../../pages/admin/AdminAuditPage.vue',
  ],
};

function buildLazyPage(registryKey, debugLabel) {
  return async () => {
    // [Debug output disabled — freeze/performance isolation | ปิด debug output ชั่วคราวเพื่อแยกปัญหา freeze/performance]
    // emitAdminRouteDebug(
    //   `${ROUTE_DEBUG_NAMESPACE} [LAZY_IMPORT_START] English: starting lazy import for admin route component | ไทย: เริ่ม lazy import สำหรับ component ของ admin route`,
    //   {
    //     registryKey,
    //     debugLabel,
    //   },
    // );

    try {
      const candidatePaths = PAGE_REGISTRY[registryKey];
      const resolvedPath = Array.isArray(candidatePaths)
        ? candidatePaths.find((path) => Boolean(PAGE_MODULES[path]))
        : null;
      const loader = resolvedPath ? PAGE_MODULES[resolvedPath] : null;

      if (!loader) {
        throw new Error(
          `${ROUTE_DEBUG_NAMESPACE} No registry entry for key: ${registryKey}, or no matching .vue file was found in src/pages/ or src/pages/admin/. Add the page file or update PAGE_REGISTRY.`,
        );
      }

      const module = await loader();

      // [Debug output disabled — freeze/performance isolation | ปิด debug output ชั่วคราวเพื่อแยกปัญหา freeze/performance]
      // emitAdminRouteDebug(
      //   `${ROUTE_DEBUG_NAMESPACE} [LAZY_IMPORT_SUCCESS] English: admin route component loaded successfully | ไทย: โหลด component ของ admin route สำเร็จ`,
      //   {
      //     registryKey,
      //     debugLabel,
      //     resolvedPath,
      //     exportKeys: Object.keys(module || {}),
      //     hasDefaultExport: Boolean(module && module.default),
      //   },
      // );

      return module;
    } catch (error) {
      console.error(
        `${ROUTE_DEBUG_NAMESPACE} [LAZY_IMPORT_ERROR] English: failed to load admin route component | ไทย: โหลด component ของ admin route ไม่สำเร็จ`,
        {
          registryKey,
          debugLabel,
          candidatePaths: PAGE_REGISTRY[registryKey] || null,
          error: error instanceof Error ? error.message : String(error),
        },
      );
      throw error;
    }
  };
}

function createAdminMeta(meta = {}) {
  const merged = {
    section: 'admin',
    requiresAuth: true,
    // English: requiresAdmin is an intent/policy marker for admin-area routes.
    //          At the moment the global router guard enforces requiresSuperAdmin directly,
    //          while requiresAdmin remains explicit metadata for route grouping, future guard
    //          expansion, and UI policy inspection.
    // Thai: requiresAdmin เป็น intent/policy marker สำหรับ admin-area routes
    //       ปัจจุบัน global router guard บังคับใช้ requiresSuperAdmin โดยตรง
    //       ส่วน requiresAdmin ยังถูกเก็บไว้เป็น metadata ชัดเจนสำหรับการจัดกลุ่ม route,
    //       การขยาย guard ในอนาคต และการตรวจ policy ฝั่ง UI/diagnostics
    requiresAdmin: true,
    layout: 'dashboard',
    navGroup: 'admin',
    accessPolicy: 'admin_role_required',
    debug_en: 'Admin route metadata helps validate whether privileged pages are isolated correctly in the dashboard router.',
    debug_th: 'metadata ของ admin route ใช้ตรวจว่าหน้าที่มีสิทธิ์ระดับแอดมินถูกแยกอย่างถูกต้องใน router ของ dashboard หรือไม่',
    ...meta,
  };

  // [Debug output disabled — freeze/performance isolation | ปิด debug output ชั่วคราวเพื่อแยกปัญหา freeze/performance]
  // emitAdminRouteDebug(
  //   `${ROUTE_DEBUG_NAMESPACE} [META_CREATED] English: created admin route metadata | ไทย: สร้าง metadata ของ admin route แล้ว`,
  //   merged,
  // );

  return merged;
}

const adminRoutes = [
  {
    path: '/admin',
    redirect: '/admin/dashboard',
    meta: createAdminMeta({
      title: 'Admin Root',
      navLabel: 'Admin',
      routePurpose_en: 'Admin root route redirects to the admin dashboard page.',
      routePurpose_th: 'route หลักของ admin จะ redirect ไปยังหน้า admin dashboard',
    }),
  },
  {
    path: '/admin/dashboard',
    name: 'AdminDashboard',
    component: buildLazyPage('AdminDashboardPage', 'AdminDashboardPage'),
    meta: createAdminMeta({
      title: 'Admin Dashboard',
      navLabel: 'Dashboard',
      requiresSuperAdmin: true,
      accessPolicy: 'super_admin_only',
      permissions: ['super_admin'],
      routePurpose_en: 'Super-admin dashboard: tenant management, user oversight, and audit log.',
      routePurpose_th: 'Super-admin dashboard: จัดการ tenant, ดูแล user และ audit log',
    }),
  },
  {
    path: '/admin/overview',
    name: 'AdminOverview',
    component: buildLazyPage('AdminOverviewPage', 'AdminOverviewPage'),
    meta: createAdminMeta({
      title: 'Admin Overview',
      navLabel: 'Overview',
      accessPolicy: 'admin_role_required',
      permissions: ['platform_admin', 'tenant_admin'],
      routePurpose_en: 'Admin overview is used to validate fleet-wide health, route guards, and privileged dashboard metrics.',
      routePurpose_th: 'หน้า admin overview ใช้ตรวจสุขภาพระดับ fleet, route guards และ metric ที่เป็นสิทธิ์ของผู้ดูแล',
    }),
  },
  {
    path: '/admin/users',
    name: 'AdminUsers',
    component: buildLazyPage('AdminUsersPage', 'AdminUsersPage'),
    meta: createAdminMeta({
      title: 'Admin Users',
      navLabel: 'Users',
      accessPolicy: 'admin_role_required',
      permissions: ['platform_admin'],
      routePurpose_en: 'Admin users page validates user-management routing and access control.',
      routePurpose_th: 'หน้า admin users ใช้ตรวจเส้นทางของการจัดการผู้ใช้และการควบคุมสิทธิ์การเข้าถึง',
    }),
  },
  {
    path: '/admin/settings',
    name: 'AdminSettings',
    component: buildLazyPage('AdminSettingsPage', 'AdminSettingsPage'),
    meta: createAdminMeta({
      title: 'Admin Settings',
      navLabel: 'Settings',
      accessPolicy: 'admin_role_required',
      permissions: ['platform_admin', 'tenant_admin'],
      routePurpose_en: 'Admin settings page validates system-level configuration routing.',
      routePurpose_th: 'หน้า admin settings ใช้ตรวจเส้นทางของการตั้งค่าระดับระบบ',
    }),
  },
  {
    path: '/admin/audit',
    name: 'AdminAudit',
    component: buildLazyPage('AdminAuditPage', 'AdminAuditPage'),
    meta: createAdminMeta({
      title: 'Admin Audit',
      navLabel: 'Audit',
      accessPolicy: 'admin_role_required',
      permissions: ['platform_admin'],
      routePurpose_en: 'Admin audit page validates debug, diagnostics, and audit-trail routing for privileged users.',
      routePurpose_th: 'หน้า admin audit ใช้ตรวจเส้นทางของ debug, diagnostics และ audit trail สำหรับผู้ใช้สิทธิ์สูง',
    }),
  },
];

// [Debug output disabled — freeze/performance isolation | ปิด debug output ชั่วคราวเพื่อแยกปัญหา freeze/performance]
// emitAdminRouteDebug(
//   `${ROUTE_DEBUG_NAMESPACE} [ROUTES_REGISTERED] English: admin route module initialized | ไทย: module ของ admin routes ถูกเตรียมใช้งานแล้ว`,
//   {
//     routeCount: adminRoutes.length,
//     routeNames: adminRoutes.map((route) => route.name || `redirect:${route.path}`),
//     paths: adminRoutes.map((route) => route.path),
//     registryKeys: Object.keys(PAGE_REGISTRY),
//     explanation_en: 'Use this snapshot to verify that admin routes are grouped separately from dashboard, robot, mission, and alert routes.',
//     explanation_th: 'ใช้ snapshot นี้ตรวจว่า admin routes ถูกแยกออกจาก dashboard, robot, mission และ alert routes อย่างถูกต้อง',
//   },
// );

export default adminRoutes;
